import filterCategoryUI from '../../templates/course_list/course_list_filter_category.mustache';
import dig from '../util/dig';
import flatpickr from 'flatpickr';
import formatHour from '../date/formatHour';
import parameterize from '../string/parameterize';
import startOfMonth from '../date/startOfMonth';
import startOfWeek from '../date/startOfWeek';
import timezone from '../date/timezone';
import titleize from '../string/titleize';
import TimeSlider from './time_slider';
import GradeStringMap, { gradesHiddenToVt4sUsers } from './course_list_grade_map';
import 'custom-event-polyfill';
import 'element-closest-polyfill';

const INPUT_SELECTOR = '.course-list-filter__filter input';

// Provides the flatpickr callback for _initDatePicker
const onChangeDatePicker = (target, input, field) => (selectedDates) => {
  const date = selectedDates[0];
  // clear the other start date inputs
  if (date) {
    input.setAttribute('checked', 'checked');
    Array.from(target.querySelectorAll('[name="start_date"]')).forEach(el => el.checked = false);
    input.dataset[field] = date.toISOString();
    if (field === 'from') {
      //set the min date on the end date picker
      target.querySelector('#choose_end_date')._flatpickr.set('minDate', date);
    }
    if (field === 'to') {
      //set the max date on the start date picker
      target.querySelector('#choose_start_date')._flatpickr.set('maxDate', date);
    }
  } else {
    input.removeAttribute('checked');
  }
};

/**
 * CourseListFilters class to manage interaction with the filter component of the course list UI
 *
 * @param {DOM element} target
 */
export default class CourseListFilters {
  constructor(target) {
    if (!target) { return; }

    this.target = target;
    this.dirty = false;
    this.allDays = [
      ['Su','Sunday'],['M','Monday'],['Tu','Tuesday'],['W','Wednesday'],['Th','Thursday'],['F','Friday'],['Sa','Saturday']
    ].map(x=> ({label: x[0], value: x[1], disabled: true}));
    this.subjects = [
      'Art',
      'English',
      'Enrichment',
      'Languages',
      'Learning Differences',
      'Math',
      'Professional Certifications',
      'Science',
      'Social Studies',
      'Technology and Coding',
      'Test Prep'
    ].map(x => ({value: x}));
  }

  /**
   * Returns boolean indicating if any filters are selected.
   */
  hasFilters() {
    return this.target && !!this.target.querySelectorAll('input:checked').length;
  }

  /**
   * Extracts selected filters from the UI
   *
   * @param {element} Container element to scope filter selection to.
   *                  defaults to the filter component target i.e. all
   * returns:
   * [{filter: "grades", value: "6th Grade"},
   * {filter: "subjects", value: "Discrete Geometry"},
   * {filter: "subjects", value: "Graphic Design"},
   * {filter: "start_times",
   *  range: { from: "1970-01-01T08:00:00.000Z", to: "1970-01-01T10:59:00.000Z"}},
   * {filter: "start_times",
   *  range: { from: "1970-01-01T11:00:00.000Z", to: "1970-01-01T13:59:00.000Z"}}]
   */
  getSelectedFilters(panel = this.target) {
    if (!this.target) { return []; }

    const inputs = Array.from(panel.querySelectorAll(INPUT_SELECTOR));
    if (inputs && inputs.length > 0) {
      return inputs
        .filter(input => input.checked)
        .map(this._inputFilterValue);
    }
  }

  /**
   * Sets selected state on filters from filters parameter
   *
   * @param {object} filters
   * e.g. {"subjects": ["math", "english"], "grades": "kindergarten"}
   */
  setSelectedFilters(filters) {
    if (!this.target) { return; }

    const inputs = this.target.querySelectorAll(INPUT_SELECTOR);
    // uncheck all the inputs
    inputs.forEach(input => input.checked = false);
    if (filters) {
      // loop through the filters and check the corresponding inputs
      for (const key of Object.keys(filters)) {
        const values = Array.isArray(filters[key]) ? filters[key] : Array.of(filters[key]);
        for (const value of values) {
          this.target.querySelector(`input[data-filter='${key}'][name='${value}']`).checked = true;
        }
      }
    }
  }

  /**
   * builds the class start time facet to send to Elastic search
   * the code that processes the returned facet values uses this as a placeholder but
   * the UI for selecting a start time is created by the _initTimeSlider function
   */
  startTimeFacet() {
    const offsetMins = new Date().getTimezoneOffset() - new Date(0).getTimezoneOffset();
    // 1970-01-01 is Standard Time, calculate offsetMins in case the client is in Daylight time now.
    const utcTime = (h, m, s) => ( new Date(1970, 0, 1, h, (m + offsetMins), s).toISOString() );

    return {
      type: 'range',
      name: `Class Times (${timezone.shorterName()})`,
      ranges: [
        { name: 'Any Time', from: utcTime(0, 0, 0), to: utcTime(23, 59, 59)}
      ]
    };
  }

  /**
   * builds the start-day facet to send to Elastic search
   * the code that processes the returned facet values uses this as a placeholder but
   * the filter values to display on screen are created in the _startDateFilterValues
   * function
   */
  startDateFacet() {
    const now = new Date(new Date().setHours(0,0,0,0));
    return {
      type: 'range',
      name: 'Class Starts',
      ranges: [
        { name: 'After Now', from: now.toISOString()}
      ]
    };
  }

  /**
   * Provides an array of values for use when building the start date filter options
   */
  _startDateFilterValues() {
    const now = new Date(new Date().setHours(0,0,0,0));
    return [
      { label: 'Starting today or later', from: now.toISOString(), type: 'radio', checked: true, id: 'this_week', name: 'start_date'},
      { label: 'Starting next week or later', from: startOfWeek(now, 1).toISOString(), type: 'radio', id: 'next_week', name: 'start_date'},
      { label: 'Starting next month or later', from: startOfMonth(now, 1).toISOString(), type: 'radio', id: 'next_month', name: 'start_date'},
      { label: 'Or specific dates:', id: 'choose_start_date', name: 'choose_start_date'}
    ];
  }

  _filterGradeValues(isVt4sUser) {
    const allGrades = Object.values(GradeStringMap);
    const filteredGrades = isVt4sUser
      ? allGrades.filter(grade => !gradesHiddenToVt4sUsers.has(grade))
      : allGrades;
    return filteredGrades
      .map(grade => ({ value: grade }));
  }

  /**
   * Returns an object from an input element with value or range data for use by the search api
   *
   * @param {DOM element} input
   */
  _inputFilterValue(input) {
    const obj = { filter: input.dataset.filter, name: input.name, type: input.type };

    const tryParseFloat = (value) => (isNaN(value) ? value : parseFloat(value));

    if (input.dataset.from) {
      obj['range'] = Object.assign({}, { from: tryParseFloat(input.dataset.from) });
    }
    if (input.dataset.to) {
      obj['range'] = Object.assign({}, obj['range'], { to: tryParseFloat(input.dataset.to)});
    }
    if (!obj['range']){
      obj['value'] = input.value;
    }
    return obj;
  }

  /**
   * Returns a sorting function based on facet type (key)
   * defaults to alpha sort if the key is not known
   *
   * @param {object} facet being used to build the filters
   */
  _facetSorter(facet) {
    const propertySort = (property = 'value') => (a, b) =>
      (a[property].localeCompare(b[property]));

    /**
     * Do not sort
     */
    const noSort = () => (0);

    /**
     * Sort function for class types aka class sizes
     * Order should be Large Group, Small Group, School@Home, VT+
     */
    const types = (a, b) => {
      const a_val = a.value.toLowerCase();
      const b_val = b.value.toLowerCase();
  
      let result = a_val.localeCompare(b_val);
  
      //All Access should always be last
      if (a_val.indexOf('access') > -1) {
        result = 1;
      }
      
      if (b_val.indexOf('access') > -1 ) {
        result = -1;
      }
      return result;
    };

    /**
     * Sort function for class durations
     * Order should be Semester class, Short class, One-time class, Ongoing class
     */
    const durations = (a, b) => {
      const a_val = a.value.toLowerCase();
      const b_val = b.value.toLowerCase();

      //if a & b start with the same letter, sort alpha
      if (a_val[0] === b_val[0]) {
        //Ongoing should always be last
        return a_val.localeCompare(b_val);
      } else {
        //invert the alpha sort so that "o" comes after "s"
        return -1 * a_val.localeCompare(b_val);
      }
    };

    return {
      'grades': noSort,
      'days_of_week': noSort,
      'start_dates': noSort,
      'class_size': types,
      'class_duration': durations,
      'price_cents': propertySort('label'),
      'start_times': propertySort('from')
    }[facet] || propertySort('value');
  }

  /**
   * Translate facet value object into object for mustache template
   *
   * @param {object}
   */
  /* eslint-disable max-lines-per-function */
  _objectifyFacet({ value, count, to, from, name, id, label, type = 'checkbox', disabled = false}) {
    const descriptions = {
      'Large Group': 'Free, live online classes (250+ students) with engaging lessons taught by experts.',
      'Small Group': 'Small class sizes (6-9 students) allow for a more personalized, collaborative learning environment.',
      'All Access': 'See classes included with Learning Membership.',
      'Ongoing Class': 'Meets regularly, but each session is self-contained. No need to attend previous or subsequent classes.',
      'Semester class': 'Meets multiple times for 8 weeks or longer.',
      'Short-term class': 'Meets multiple times for less than 8 weeks.',
      'One-time class': 'Meets only once for a single session.'
    };
    const description = descriptions[value];

    const radioFacet = () => (
      {id, label, name, count, from, to, type, for: id, value: from, description}
    );
    const rangeFacet = () => {
      const key = id || parameterize(name);
      return { id: key, label: (label || name), name: key, for: key, from, to, type, description};
    };
    const valueFacet = () => {
      const key = id || parameterize(value);
      const labelVal = (label || name || value);
      return { id: key, label: labelVal, name: key, for: key, value, type, description, disabled};
    };
    // if type is radio we need to set name to get mutual exclusive behaviour
    if (from || to) {
      if (type === 'radio') {
        return radioFacet();
      } else {
        //if we have to and from, this a range facet else it is a value facet
        return rangeFacet();
      }
    } else {
      return valueFacet();
    }
  }
  /* eslint-enable max-lines-per-function */

  /**
   * Sorts filter categories based on business requirements
   */
  _orderCategories(a, b) {
    const filterOrder = {
      'class_size': 1,
      'grades': 2,
      'subjects': 3,
      'days_of_week': 4,
      'start_times': 5,
      'class_duration': 6,
      'start_dates': 7
    };
    const cat1Order = filterOrder[a.category] || 9;
    const cat2Order = filterOrder[b.category] || 9;
    return cat1Order - cat2Order;
  }

  /**
   * Maps the ElasticSearch facet objects into objects for the mustache templates
   *
   * @param {array} facets
   * @param {object} initialFilters - used to pre-select filters
   */
  _buildFilterCategories({ facets, initialFilters, isVt4sUser }) {

    /**
     * Formats facet heading for UI display
     * the latter replace function is to fix Class Times (pdt) => Class Times (PDT)
     *
     * @param {} key
     */
    const facetHeading = (key) => {
      return titleize(dig([key, 0, 'name'], facets)).replace(/\((.*)\)/, a => a.toUpperCase());
    };

    /**
     * Returns facet values for UI display.
     * These may come either from the server facet values returned,
     * or generated by a function, or filter here.
     *
     * @param {} key
     */
    // eslint-disable-next-line complexity -- We will remove 3 filters soon, so this will be simpler
    const facetFilters = (key) => {
      const entries = dig([key, 0, 'data'], facets);

      if(!entries) return [];

      switch (key) {
        case 'start_dates':
          return this._startDateFilterValues();
        case 'start_times':
          return [{from: 0, to: 24, id: 'time_slider', type: 'hidden',  name: 'class_time' }];
        case 'grades':
          return this._filterGradeValues(isVt4sUser);
        case 'days_of_week':
          return this.allDays.map((day) =>{
            const entry = entries.find(e => e.value === day.value);
            if (entry) {
              Object.assign(day, entry, {disabled: false});
            }
            return day;
          });
        case 'parent_group': {
          const articles = ['a', 'and', 'but', 'or', 'of', 'the', 'to'];
          const collectionEntries = entries.filter((entry) => (
            entry.value.startsWith('curation_list-') && !entry.value.includes('varsity_tutors_for_schools')
          )).slice(0,10);
          const vt4sTag = entries.find((entry) => entry.value === 'curation_list-varsity_tutors_for_schools');
          collectionEntries.push(vt4sTag);
          return collectionEntries.map((tag) => {
            return Object.assign(tag, { label: titleize(tag.value.replace('curation_list-', ''), articles) });
          });
        }
        case 'class_size':
          return entries.filter(entry => entry.value != 'School@Home' && entry.value != 'VT+');
        case 'subjects':
          return this.subjects.filter(entry => {
            // VT4S users shouldn't see the "Professional Certifications" subject option
            const isProfCertsAndBlocked = isVt4sUser && entry.value === 'Professional Certifications';
            return !isProfCertsAndBlocked;
          });
        default:
          return entries;
      }
    };

    //transform the result list payload and sort the filters
    const data = Object.keys(facets).map(key => (
      {
        heading: facetHeading(key),
        category: key,
        filters: facetFilters(key)
          .map(this._objectifyFacet)
          .sort(this._facetSorter(key))
      }
    ));

    // pre-select filters if they were passed to the page
    if (initialFilters) {
      Object.keys(initialFilters).forEach(key => {
        const category = data.find(el => el.category === key);
        if (category) {
          category.filters.forEach(obj => {
            const init = initialFilters[key];
            // init can be an array or a string.  Ensure it is an array
            // to make the comparison simpler
            const filters = Array.isArray(init) ? init : Array.of(init);
            if (filters.includes(obj.value) || filters.includes(obj.id)) {
              obj.checked = true;
            }
          });
        }
      });
    }

    return data.sort(this._orderCategories);
  }

  /**
    * Initializes the time slider control if the placeholder exists
    */
  _initTimeSlider() {
    const container = document.querySelector('.js-start_times .course-list-filter__filter');
    if (container instanceof HTMLElement) {
      const wrapper = container.appendChild(document.createElement('div'));
      wrapper.classList.add('time-slider');
      wrapper.dataset.min = 0;
      wrapper.dataset.max = 24;

      //we are offsetting the min/max on the input to account for a visual gap and a
      //min time delta of 2 hours
      const leftHandle = wrapper.appendChild(document.createElement('span'));
      leftHandle.classList.add('handle', 'left');
      leftHandle.dataset.min = 0;
      leftHandle.dataset.max = 25;

      const highlight = wrapper.appendChild(document.createElement('span'));
      highlight.classList.add('highlight');

      const rightHandle = wrapper.appendChild(document.createElement('span'));
      rightHandle.classList.add('handle', 'right');
      rightHandle.dataset.min = -2;
      rightHandle.dataset.max = 25;


      // make a hidden input to track our end_time.
      const end_time = container.appendChild(document.createElement('input'));
      end_time.type = 'hidden';
      end_time.dataset.filter = 'end_times';
      const inputs = container.querySelectorAll('input');

      document.addEventListener('ts-change',(e) => this._handleTimeSliderChange(inputs, e));
      new TimeSlider(document.querySelector('.time-slider'));
    }
  }

  /**
   * Initializes the datepicker control(s) if it exists
   */
  /* istanbul ignore next */
  _initDatePicker() {
    const startDateInput = this.target.querySelector('#choose_start_date');
    const target = this.target;
    if (startDateInput) {
      const parent = startDateInput.parentElement;
      const startOfDay = new Date().setHours(0,0,0,0);
      startDateInput.placeholder = 'Starts on or after';

      //we need to add an end date input here as well
      const endDateInput = document.createElement('input');
      endDateInput.type = 'text';
      endDateInput.placeholder = 'Ends by';
      endDateInput.id = 'choose_end_date';
      endDateInput.name = 'choose_end_date';
      endDateInput.dataset.filter = 'end_date';

      //the date pickers need to be wrapped in a div so they display side by side
      const inputWrapper = document.createElement('div');
      parent.insertBefore(inputWrapper, parent.querySelector('label'));
      inputWrapper.style.display = 'flex';
      inputWrapper.appendChild(startDateInput);
      inputWrapper.appendChild(endDateInput);

      const fpStart = flatpickr(startDateInput, {
        minDate: startOfDay,
        dateFormat: 'D, M j',
        onChange: onChangeDatePicker(target, startDateInput, 'from')
      });

      const fpEnd = flatpickr(endDateInput, {
        minDate: startOfDay,
        dateFormat: 'D, M j',
        onChange: onChangeDatePicker(target, endDateInput, 'to')
      });

      target.addEventListener('change', (e) => {
        // clear the flatpickr input
        if (e.target.dataset.filter === 'start_dates' && e.target !== startDateInput && e.target !== endDateInput) {
          fpStart.clear();
          fpStart.set('maxDate', null);
          fpEnd.clear();
          fpEnd.set('minDate', startOfDay);
        }
      });

    }
  }

  /*
   * Clears any selected filters for the given category
   */
  _clearFilterPanel(panel){
    if (panel) {
      for (const input of panel.querySelectorAll('input[checked], input:checked')) {
        this.dirty = true;
        input.checked = false;
        if (input._flatpickr) {
          input._flatpickr.clear();
          if (input.id === 'choose_start_date') {
            input._flatpickr.set('maxDate', null);
          }
          if (input.id === 'choose_end_date') {
            input._flatpickr.set('minDate', new Date().setHours(0,0,0,0));
          }

        }
      }
    }
  }

  /*
   * Raises the filter event for use by the course list component
   */
  _raiseFilterEvent(){
    if (this.dirty) {
      this.dirty = false;
      const event = new CustomEvent('filter');
      document.dispatchEvent(event);
    }
  }

  /*
   * Closes all filter modals
   */
  /* istanbul ignore next */
  _closeFilterModals(){
    for (const trigger of this.target.querySelectorAll('.filter-modal__toggle')) {
      trigger.checked = false;
    }
    this.target.querySelector('.course-list__filters-scroll-container').classList.remove('filter--open');
  }

  /* istanbul ignore next */
  _scrollIntoView(element){
    const {right} = element.getClientRects()[0];
    if (right > window.innerWidth) {
      this.target.querySelector('.course-list__filters-scroll-container').scrollBy(300,0);
    }
  }

  _updateFilterPanelLabels(){
    const joinSelected = (nodeList) => ( Array.from(nodeList).map(x => x.dataset.label).join(', '));

    const oneOrMany = (nodeList, label) => {
      if (nodeList.length == 1) {
        return Array.from(nodeList)[0].dataset.label;
      } else {
        return `${nodeList.length} ${label}`;
      }
    };

    const daysAndTimes = (nodeList) => {
      const days = Array.from(nodeList).filter( x => x.dataset.filter === 'days_of_week');
      const class_times = Array.from(nodeList).find( x => x.dataset.filter === 'start_times');
      let dayString = joinSelected(days) || 'Any day';
      let timeString = 'Any time';
      if (class_times) { timeString =
          `${class_times.dataset.fromLabel} - ${class_times.dataset.toLabel}`; }
      if (days.length % 7 !== 0) { dayString = joinSelected(days); }
      return `${dayString}; ${timeString}`;
    };

    const datesAndDuration = (nodeList) => {
      const duration = Array.from(nodeList).filter( x => x.dataset.filter === 'class_duration');
      const startDate = Array.from(nodeList).find( x => x.dataset.filter === 'start_dates');
      const endDate = Array.from(nodeList).find( x => x.dataset.filter === 'end_date');
      let durationString = 'Any duration';
      let startString = 'Any start date';
      if (duration.length > 0) { durationString = oneOrMany(duration, 'durations'); }

      const dtf = Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

      if (startDate && endDate) {
        const from = dtf.format(new Date(startDate.dataset.from));
        const to = dtf.format(new Date(endDate.dataset.to));
        startString = `${from} - ${to}`;
      } else if (startDate && startDate.dataset.from) {
        startString = `Starting on or after ${dtf.format(new Date(startDate.dataset.from))}`;
      } else if (endDate && endDate.dataset.to) {
        startString = `Ends by ${dtf.format(new Date(endDate.dataset.to))}`;
      }
      return `${durationString}; ${startString}`;
    };

    const labelFunction = {
      'dates-and-duration': datesAndDuration,
      'days-and-times': daysAndTimes,
      'grade-levels': oneOrMany,
      'grades': oneOrMany,
      'parent_group': oneOrMany,
      'price': joinSelected,
      'subjects': oneOrMany,
      'types': oneOrMany
    };
    const panels = Array.from(this.target.querySelectorAll('.filter-tab'));
    let totalFilters = 0;
    for (const panel of panels) {
      const label = panel.querySelector('.tab-label');
      const selected = Array.from(panel.querySelectorAll('input')).filter( x => x.checked);
      totalFilters += selected.length;
      if (selected.length == 0) {
        label.classList.remove('active');
        label.innerText = label.dataset.default;
      } else {
        label.innerText = labelFunction[label.htmlFor].call(this, selected, label.dataset.default);
        label.classList.add('active');
      }
    }
    const modalFiltersTrigger = this.target.querySelector('.modal-filters__show');
    const triggerCountLabel = this.target.querySelector('#filter-count');
    if (totalFilters) {
      modalFiltersTrigger.classList.add('active');
      triggerCountLabel.innerText = ` (${totalFilters})`;
    } else {
      modalFiltersTrigger.classList.remove('active');
      triggerCountLabel.innerText = '';
    }
  }

  /* eslint-disable complexity */
  /* istanbul ignore next */
  _handleClickEvent(e){
    // quick and simple dirty state management
    if (e.target.tagName === 'INPUT' && e.target.dataset.filter) { this.dirty = true; }

    if (e.target.classList.contains('modal-filters__show')) {
      this.target.querySelector('.course-list__filters--bar').classList.add('modal__fullscreen');
      document.documentElement.classList.add('html--modal-open-full');
    }

    if (e.target.classList.contains('tab-label')) {
      if (!document.getElementById(e.target.htmlFor).checked) {
        //mutually exclusive checkboxes
        //we are clicking on an unchecked element
        this._closeFilterModals();
        this._updateFilterPanelLabels();
        this._raiseFilterEvent();
      }
    }
    //apply button (close)
    if (e.target.classList.contains('apply')) {
      e.preventDefault();
      const el = this.target.querySelector('.modal__fullscreen');
      if (el){
        window.scrollTo(0,0);
        el.classList.add('closing');
        document.documentElement.classList.remove('html--modal-open-full');
        window.setTimeout(function() {
          el.classList.remove('modal__fullscreen', 'closing');
        }, 600);
      }
      this._closeFilterModals();
      this._updateFilterPanelLabels();
      this._raiseFilterEvent();
    }
    //clear modal when clear pressed
    if (e.target.classList.contains('clear')) {
      e.preventDefault();
      const panel = e.target.closest('.filter-modal') || e.target.closest('.modal__fullscreen');
      if (panel)  {
        this._clearFilterPanel(panel);
      }
    }
  }
  /* eslint-enable complexity */

  /*
    * handles the custom change event from the time slider control
    */
  _handleTimeSliderChange(inputs, {detail: {property, value}}){
    // quick and simple dirty state management
    this.dirty = true;
    const offsetMins = new Date().getTimezoneOffset() - new Date(0).getTimezoneOffset();
    // 1970-01-01 is Standard Time, calculate offsetMins in case the client is in Daylight time now.
    const utcTime = (h, m, s) => ( new Date(1970, 0, 1, h, (m + offsetMins), s).toISOString() );
    [...inputs].forEach(input => {
      input.checked = true;
      input.dataset[property + 'Label'] = formatHour(value);
      input.dataset[property] = utcTime(value,0,0);
      if (input.dataset.fromLabel === '12 am' && input.dataset.toLabel === '12 am') {
        input.checked = false;
      }
    });
  }

  /**
   * Sets up event handlers for clearing and closing panels, and clicking filters
   */
  /* istanbul ignore next */
  _setupEventHandlers(){
    this.target.addEventListener('click', (e) => (this._handleClickEvent(e)));

    document.addEventListener('click', (e) => {
      //calendar is modal, so outside the filter dom heirarchy
      const flatPickrTarget = (el) => {
        //do we have any calendars?
        const calendar = document.querySelector('.flatpickr-calendar');
        if (calendar) {
          //checks to see if any parent of the target is a calendar.
          return !!el.closest('.flatpickr-calendar');
        } else {
          return false;
        }
      };
      // click outside of the filter bar
      if (e.target.classList.contains('course-list__filters-scroll-container') ||
        ( !this.target.contains(e.target) && !flatPickrTarget(e.target))){
        this._closeFilterModals();
        this._updateFilterPanelLabels();
        this._raiseFilterEvent();
      }
    });

    this.target.addEventListener('change', (e) => {
      if (e.target.classList.contains('filter-modal__toggle') && (e.target.checked)) {
        this.target.querySelector('.course-list__filters-scroll-container').classList.add('filter--open');
        this._scrollIntoView(this.target.querySelector('.filter-modal__toggle:checked ~ .filter-modal'));
      }
    });
  }

  /**
   * Sets the column count property for filter category
   */
  _setColumnCount(cat, cat_target) {
    if (window.innerWidth > 600) {
      cat_target.style.setProperty('--column-count',  Math.ceil(cat.filters.length / 7));
    } else {
      // allocating 40px for each grade item so that the height can be adjusted
      // as the list grows to display in 2 columns.
      cat_target.style.setProperty('height',  40 * (Math.ceil(cat.filters.length / 2)));
      cat_target.style.setProperty('--column-count',  2);
    }
  }

  /**
   * Renders the filters UI to the target element
   *
   * @param {object} facets
   * @param {object} initialFilters
   */
  render({ facets, initialFilters, isVt4sUser }) {
    if (!this.target) { return; }

    const categories = this._buildFilterCategories({ facets, initialFilters, isVt4sUser });

    for (const cat of categories) {
      //only render for facets we have a placeholder for
      const cat_target = this.target.querySelector(`.js-${cat.category}`);
      if (cat_target) {
        cat_target.innerHTML = filterCategoryUI(cat);
        // the grades panel flex wraps. The width is controlled by the --column-count variable
        if (cat.category === 'grades') {
          this._setColumnCount(cat, cat_target);
        }
        // If there are no filter categories, hide facet
        if(cat.filters.length === 0) {
          const filterTab = cat_target.closest('.filter-tab');
          if (filterTab)  {
            filterTab.style.display = 'none';
          }
        }
      }
    }
    this._updateFilterPanelLabels();
    this._initDatePicker();
    this._initTimeSlider();
    this._setupEventHandlers();
    this.dirty = false;
  }
}
